import type { MouseEventHandler } from 'react';
import type { TooltipRenderProps } from 'react-joyride';

import { use, useCallback, useEffect, useRef } from 'react';
import TagManager from 'react-gtm-module';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { twMerge } from 'tailwind-merge';

import { getTestProps, sendEventAndClear } from '../../../lib/helpers';

import useSelectedSpace from '../../../hooks/useSelectedSpace';

import UserContext from '../../../contexts/UserContext';

import {
  CaretLeftIcon,
  CaretRightIcon,
  CloseIcon,
} from '../../../images/shapes';

import Button from '../../Button/Button';
import Tooltip from '../../Tooltip/Tooltip';

import './tutorial-tooltip.css';

interface TutorialTooltipProps extends TooltipRenderProps {
  /**
   * Step data
   */
  step: TooltipRenderProps['step'] & {
    page?: string;
    data?: {
      previous?: string;
      next?: string;
    };
    testId?: string;
  };
  /**
   * Component test id
   */
  testId?: string;
}

const TutorialTooltip = (props: TutorialTooltipProps) => {
  const {
    continuous,
    index,
    step,
    backProps,
    closeProps,
    primaryProps,
    tooltipProps,
    skipProps,
    size = 0,
    testId = '',
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { buildUrlWithSpace } = useSelectedSpace();
  const { baseUserEventData } = use(UserContext);
  const stepSentRef = useRef<number>(undefined);

  const handleNavigate = useCallback(
    (path: string = '') => {
      navigate(buildUrlWithSpace(path === 'dashboard' ? '' : path));
    },
    [navigate, buildUrlWithSpace],
  );

  const onSkip = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'skip_tutorial',
        },
      });
      skipProps?.onClick?.(e);
    },
    [skipProps],
  );

  useEffect(() => {
    if (stepSentRef.current !== index) {
      stepSentRef.current = index;
      sendEventAndClear(
        {
          event: 'tutorial_step_reached',
          step_index: index,
          step_title: step.title,
          step_page: step.page,
        },
        baseUserEventData,
      );
    }
  }, [index, step.title, step.page, baseUserEventData]);

  return (
    <div
      className={twMerge(
        'bg-white dark:bg-slate-700 p-4',
        'rounded-lg relative min-w-[300px] max-w-[calc(100vw-30px)]',
      )}
      {...getTestProps(testId || props?.step?.testId, `container-${index}`)}
      {...tooltipProps}
    >
      <Button
        buttonColor="borderless"
        id="close"
        additionalClasses="absolute right-2 top-2 p-1! hover:opacity-30"
        {...getTestProps(
          testId || props?.step?.testId,
          `close-${index}`,
          'testId',
        )}
        {...closeProps}
      >
        <CloseIcon
          className="w-3 h-3 text-slate-900 dark:text-slate-200"
          aria-hidden="true"
        />
      </Button>

      {step?.title && (
        <div
          className="text-black font-bold text-xl dark:text-slate-200 mr-4"
          {...getTestProps(testId || props?.step?.testId, `title-${index}`)}
        >
          {step.title}
        </div>
      )}

      <div
        dangerouslySetInnerHTML={{ __html: step?.content || '' }}
        className="text-black dark:text-slate-200 py-4 tutorial-content"
        {...getTestProps(testId || props?.step?.testId, `content-${index}`)}
      />

      <div className="flex flex-row">
        {skipProps && (
          <Button
            id="skip"
            buttonColor="borderless"
            {...skipProps}
            onClick={onSkip}
            {...getTestProps(
              testId || props?.step?.testId,
              `skip-${index}`,
              'testId',
            )}
          >
            {t('Tutorials.Skip')}
          </Button>
        )}

        <div className="ml-auto flex flex-row">
          {index === 0 && props?.step?.data?.previous && (
            <Tooltip
              tooltip={`Go to ${props?.step?.data?.previous}`}
              tooltipPlacement={'topCenter'}
              phoneTooltipPlacement={'topCenter'}
            >
              <Button
                iconImage={<CaretLeftIcon className="h-3 w-3" />}
                additionalClasses={'mx-2'}
                onClick={() => handleNavigate(props?.step?.data?.previous)}
                {...getTestProps(
                  testId || props?.step?.testId,
                  `navigate-previous`,
                  'testId',
                )}
              />
            </Tooltip>
          )}

          {index > 0 && (
            <Button
              id="back"
              additionalClasses="mx-2"
              {...backProps}
              {...getTestProps(
                testId || props?.step?.testId,
                `back-${index}`,
                'testId',
              )}
            >
              {t('Tutorials.Back')}
            </Button>
          )}

          {continuous && (
            <Button
              id="next"
              {...primaryProps}
              {...getTestProps(
                testId || props?.step?.testId,
                `next-${index}`,
                'testId',
              )}
            >
              <span
                {...getTestProps(
                  testId || props?.step?.testId,
                  `next-text-${index}`,
                )}
              >
                {props?.isLastStep ? t('Tutorials.Last') : t('Tutorials.Next')}
              </span>
              {props?.step?.showProgress && (
                <span
                  className="ml-2"
                  {...getTestProps(
                    testId || props?.step?.testId,
                    `progress-${index}`,
                  )}
                >
                  {index + 1} / {size}
                </span>
              )}
            </Button>
          )}

          {props?.isLastStep && props?.step?.data?.next && (
            <Tooltip
              tooltip={`Go to ${props?.step?.data?.next}`}
              tooltipPlacement={'topCenter'}
              phoneTooltipPlacement={'topCenter'}
            >
              <Button
                iconImage={<CaretRightIcon className="h-3 w-3" />}
                additionalClasses={'mx-2'}
                {...getTestProps(
                  testId || props?.step?.testId,
                  `navigate-next`,
                  'testId',
                )}
                onClick={() => handleNavigate(props?.step?.data?.next)}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

export default TutorialTooltip;
